/* Alpha Works motion: decorative acoustic geometry, never simulated audio. */
if (!window.awShopify?.designMode && window.awShopify?.motion !== false && !window.__alphaWorksMotion && document.querySelector('#main')) {
  window.__alphaWorksMotion = true;

  const root = document.documentElement;
  const main = document.querySelector('#main');
  const header = document.querySelector('.site-header');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const revealed = new WeakSet();
  const awaitingReveal = new Set();
  const watchedVisuals = new Set();
  const visibleVisuals = new Set();
  let scanQueued = false;
  let pointerFrame = 0;
  let scrollFrame = 0;
  let pointerTarget = null;
  let pointerPosition = null;
  let currentRoute = null;
  let routeAnimation = null;

  const enabled = () => !reducedMotion.matches;
  const revealSelector = '.section-heading, .category-tile, .product-card, .lifestyle-card, .brand-note, .product-features, .product-story, .spec-section, .story-intro, .story-photo, .story-values > div, .support-cards > a, .contact-section, .support-faq, .download-card';
  const pointerSelector = '.hero, .main-product-image, .product-image-wrap, .category-tile';

  function reveal(element) {
    element.classList.remove('aw-reveal-pending');
    element.classList.add('aw-revealed');
    revealed.add(element);
    awaitingReveal.delete(element);
    revealObserver?.unobserve(element);
  }

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
  }, { threshold: 0.06, rootMargin: '0px 0px 24px 0px' }) : null;

  const visualObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      entry.target.classList.toggle('aw-in-view', entry.isIntersecting);
      if (entry.target.matches('.hero')) entry.target.querySelector('.hero-visual')?.classList.toggle('aw-in-view', entry.isIntersecting);
      if (entry.isIntersecting) visibleVisuals.add(entry.target);
      else visibleVisuals.delete(entry.target);
    });
    scheduleScroll();
  }, { rootMargin: '50px' }) : null;

  function resetPointer() {
    if (pointerTarget) {
      pointerTarget.style.removeProperty('--aw-pointer-x');
      pointerTarget.style.removeProperty('--aw-pointer-y');
      pointerTarget.style.removeProperty('--aw-pointer-turn');
      pointerTarget.classList.remove('aw-pointer-active');
    }
    pointerTarget = null;
    pointerPosition = null;
  }

  function syncMotion() {
    // A redundant class write still notifies the campaign observer. Keep scans
    // read-only when the enhancement state has already been applied.
    if (!root.classList.contains('aw-enhanced')) root.classList.add('aw-enhanced');
    root.classList.toggle('aw-motion-enabled', enabled());
    if (!enabled()) {
      resetPointer();
      routeAnimation?.cancel();
      Array.from(awaitingReveal).forEach(reveal);
      watchedVisuals.forEach(element => element.style.removeProperty('--aw-scroll-y'));
    }
    scheduleScroll();
  }

  function addSoundGeometry(visual) {
    if (visual.querySelector('.aw-equalizer')) return;
    const hero = visual.closest('.hero');
    // Keep the homepage hero aligned with the clean editor preview; motion remains active elsewhere.
    if (hero) return;
    hero.classList.add('aw-chaos-hero');
    const field = document.createElement('div');
    field.className = 'aw-chaos-canvas';
    field.setAttribute('aria-hidden', 'true');
    field.innerHTML = '<div class="aw-chaos-orbit"></div><div class="aw-chaos-halo"></div><div class="aw-chaos-ribbon">EVERYDAY, AMPLIFIED.</div><span class="aw-chaos-corner">AW / MY</span>';
    hero.querySelector('.hero-inner').prepend(field);
    // Product PNGs already carry their own alpha channel. A fixed silhouette
    // cuts through different models, including the W88 carrying handle.
    const beat = document.createElement('div');
    beat.className = 'aw-equalizer';
    beat.setAttribute('aria-hidden', 'true');
    [11, 19, 29, 38, 26, 17, 32, 22, 12].forEach((height, index) => {
      const bar = document.createElement('i');
      bar.style.setProperty('--aw-bar-height', `${height}px`);
      bar.style.setProperty('--aw-bar-delay', `${index * -0.17}s`);
      beat.append(bar);
    });
    visual.prepend(beat);
  }

  function scan() {
    scanQueued = false;
    main.querySelectorAll('.hero-visual').forEach(addSoundGeometry);
    syncMotion();

    awaitingReveal.forEach(element => {
      if (!element.isConnected) {
        revealObserver?.unobserve(element);
        awaitingReveal.delete(element);
      }
    });
    watchedVisuals.forEach(element => {
      if (!element.isConnected) {
        visualObserver?.unobserve(element);
        watchedVisuals.delete(element);
        visibleVisuals.delete(element);
      }
    });

    if (enabled() && revealObserver) {
      main.querySelectorAll(revealSelector).forEach(element => {
        if (revealed.has(element) || awaitingReveal.has(element)) return;
        // A short stagger belongs to a collection; full sections stay immediate.
        const isCollectionItem = element.matches('.product-card, .category-tile, .download-card, .story-values > div');
        const index = isCollectionItem ? Array.from(element.parentElement.children).indexOf(element) : 0;
        element.style.setProperty('--aw-reveal-delay', `${Math.min(index % 5, 3) * 55}ms`);
        element.classList.add('aw-reveal-pending');
        awaitingReveal.add(element);
        revealObserver.observe(element);
      });
    }

    main.querySelectorAll('.hero, .lifestyle-card, .story-photo').forEach(element => {
      if (watchedVisuals.has(element)) return;
      watchedVisuals.add(element);
      if (visualObserver) visualObserver.observe(element);
      else {
        element.classList.add('aw-in-view');
        visibleVisuals.add(element);
      }
    });

    const route = location.pathname || '/';
    if (route !== currentRoute) {
      resetPointer();
      routeAnimation?.cancel();
      if (currentRoute !== null && enabled() && main.animate) {
        routeAnimation = main.animate([{ opacity: 0.65 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
      currentRoute = route;
    }
  }

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(scan);
  }

  function updateScroll() {
    scrollFrame = 0;
    header?.classList.toggle('aw-scrolled', scrollY > 12);
    if (!enabled() || !finePointer.matches || document.hidden) return;
    const updates = [];
    visibleVisuals.forEach(element => {
      if (!element.isConnected) return;
      const box = element.getBoundingClientRect();
      const position = (innerHeight * 0.5 - box.top - box.height * 0.5) / innerHeight;
      const range = element.matches('.hero') ? 76 : 14;
      const strength = element.matches('.hero') ? 150 : 26;
      updates.push([element, Math.max(-range, Math.min(range, position * strength))]);
    });
    updates.forEach(([element, offset]) => element.style.setProperty('--aw-scroll-y', `${offset.toFixed(2)}px`));
  }

  function scheduleScroll() {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
  }

  document.addEventListener('pointermove', event => {
    if (!enabled() || !finePointer.matches || event.pointerType === 'touch' || document.querySelector('dialog[open]')) {
      resetPointer();
      return;
    }
    const target = event.target instanceof Element ? event.target.closest(pointerSelector) : null;
    if (!target || !main.contains(target)) { resetPointer(); return; }
    if (target !== pointerTarget) {
      resetPointer();
      pointerTarget = target;
      target.classList.add('aw-pointer-active');
    }
    pointerPosition = { x: event.clientX, y: event.clientY };
    if (pointerFrame) return;
    pointerFrame = requestAnimationFrame(() => {
      pointerFrame = 0;
      if (!pointerTarget?.isConnected || !pointerPosition) return;
      const box = pointerTarget.getBoundingClientRect();
      const strength = pointerTarget.matches('.hero') ? 26 : 6;
      const x = ((pointerPosition.x - box.left) / box.width - 0.5) * strength * 2;
      const y = ((pointerPosition.y - box.top) / box.height - 0.5) * strength * 2;
      pointerTarget.style.setProperty('--aw-pointer-x', `${x.toFixed(2)}px`);
      pointerTarget.style.setProperty('--aw-pointer-y', `${y.toFixed(2)}px`);
      pointerTarget.style.setProperty('--aw-pointer-turn', `${(x / 9).toFixed(2)}deg`);
    });
  }, { passive: true });

  // Reveals never hide a destination reached with a keyboard or the skip link.
  document.addEventListener('focusin', event => {
    let element = event.target instanceof Element ? event.target : null;
    while (element && element !== main) {
      if (awaitingReveal.has(element)) reveal(element);
      element = element.parentElement;
    }
  });
  document.addEventListener('pointerout', event => { if (!event.relatedTarget) resetPointer(); });
  window.addEventListener('blur', resetPointer);
  window.addEventListener('scroll', scheduleScroll, { passive: true });
  window.addEventListener('resize', scheduleScroll, { passive: true });
  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('aw-tab-hidden', document.hidden);
    if (document.hidden) resetPointer();
    else scheduleScroll();
  });
  reducedMotion.addEventListener('change', () => { syncMotion(); scan(); });
  finePointer.addEventListener('change', () => {
    resetPointer();
    watchedVisuals.forEach(element => element.style.removeProperty('--aw-scroll-y'));
    scheduleScroll();
  });

  const contentObserver = new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' && (record.addedNodes.length || record.removedNodes.length))) queueScan();
    records.forEach(record => {
      if (record.type === 'attributes' && enabled() && record.target.matches('.main-product-image > img') && record.target.animate) {
        record.target.animate([{ opacity: 0.45 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
      }
    });
  });
  contentObserver.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });

  const dialogObserver = new MutationObserver(() => {
    const overlayOpen = !!document.querySelector('dialog[open]');
    root.classList.toggle('aw-overlay-open', overlayOpen);
    if (overlayOpen) resetPointer();
  });
  document.querySelectorAll('dialog').forEach(dialog => dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] }));

  scan();
}

